import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addImageElement, setSelectedImageId } from '../../redux/TabTile/TabActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ImageElementType } from '../../redux/TabTile/TabStateInterfaces';
import { RootState } from '../../redux';
import './DashBoard.css';

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  innerDimensions: { width: number; height: number };
  tabId: number;
  setSelectedObject: (obj: string | null) => void;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
  innerDimensions,
  tabId,
  setSelectedObject,
}) => {
  const dispatch = useDispatch();
  const gridSize = useSelector((state: RootState) => state.tabTileProps.dashGridSize);

  const [mode, setMode] = useState<'url' | 'file'>('file');
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): boolean => {
    const MAX_SIZE = 5 * 1024 * 1024;
    if (!file) return false;
    if (file.size > MAX_SIZE) {
      setError('File size exceeds 5MB limit');
      return false;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return false;
    }
    setError(null);
    return true;
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setImageFile(file);
    }
    e.target.value = '';
  };

  const validImageUrl = async (url: string): Promise<boolean> => {
    try {
      const res = await fetch(url, { method: 'HEAD' });
      const contentType = res.headers.get('content-type');

      return res.ok && !!contentType && contentType.startsWith('image/');
      // or: return res.ok && contentType?.startsWith("image/") === true;
    } catch {
      return false;
    }
  };

  const handleAddImage = (src: string) => {
    // Set a default width and height in grid units
    const gridWidth = 16;
    const gridHeight = 10;

    // Calculate position in grid units
    const x = Math.floor((innerDimensions.width / gridSize.x - gridWidth) / 2);
    const y = Math.floor((innerDimensions.height / gridSize.y - gridHeight) / 2);

    const newImage: ImageElementType = {
      id: `img-${Date.now()}`,
      src,
      x,
      y,
      width: gridWidth,
      height: gridHeight,
      scaling: 'contain',
      type: 'image',
      description,
      borderRadius: 12,
      containerProps: { borderRadius: 12 },
    };

    dispatch(addImageElement(tabId, newImage));
    dispatch(setSelectedImageId(tabId, newImage.id));
    setSelectedObject('image');
  };

  const simulateUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);

    let uploaded = 0;
    const chunkSize = file.size / 100;

    const interval = setInterval(() => {
      uploaded += chunkSize;
      const progress = Math.min((uploaded / file.size) * 100, 100);
      setUploadProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(async () => {
          try {
            const base64 = await fileToBase64(file);
            handleAddImage(base64);
            setImageFile(null);
            setDescription('');
            onClose();
          } catch {
            setError('Failed to convert image');
          } finally {
            setIsUploading(false);
          }
        }, 300);
      }
    }, 50);
  };

  const handleAddClick = async () => {
    if (mode === 'url') {
      if (!imageUrl.trim()) {
        setError('Please enter an image URL');
        return;
      }
      const valid = await validImageUrl(imageUrl.trim());
      if (!valid) {
        setError('Image Url is not valid');
        return;
      }

      handleAddImage(imageUrl.trim());
      setImageUrl('');
      onClose();
    } else if (mode === 'file' && imageFile) {
      validateFile(imageFile) && simulateUpload(imageFile);
    } else {
      setError('Please select an image file');
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-title-row">
            <h3 className="modal-title">Edit Image Object</h3>
            <FontAwesomeIcon
              icon={faTimes}
              className="close-icon"
              onClick={onClose}
              title="Close"
            />
          </div>

          <div className="mode-selector">
            {['file', 'url'].map((m) => (
              <button
                key={m}
                className={`mode-button ${mode === m ? 'active' : ''}`}
                onClick={() => {
                  setMode(m as 'file' | 'url');
                  setError(null);
                }}
              >
                {m === 'file' ? 'Insert Image File' : 'Link to Image'}
              </button>
            ))}
          </div>

          <div className="input-section">
            {mode === 'url' && (
              <div className="url-input-group fade-section visible">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter image URL"
                  className="url-input"
                />
              </div>
            )}

            {mode === 'file' && (
              <div className="file-input-group fade-section visible">
                <p className="file-input-text">
                  {imageFile ? imageFile.name : 'Choose an image file (max 5MB)...'}
                </p>
                <label className="file-input-label">
                  Choose
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input-hidden"
                  />
                </label>
              </div>
            )}

            {error && (
              <div
                className="error-message"
                style={{ color: 'red', marginTop: 5, textAlign: 'start' }}
              >
                {error}
              </div>
            )}

            <div className="description-input-group fade-section visible" style={{ marginTop: 10 }}>
              <label htmlFor="description">Description</label>
              <input
                id="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                className="description-input"
              />
            </div>

            <div className="input-section-spacer" />
          </div>

          <div className="button-group">
            <button className="ok-button" onClick={handleAddClick}>
              Add Image
            </button>
          </div>
        </div>
      </div>

      {isUploading && imageFile && (
        <div className="upload-progress-overlay">
          <div className="upload-progress-container">
            <div className="upload-progress-header">
              <h4>Uploading File</h4>
              <p>{imageFile.name}</p>
            </div>
            <div className="upload-progress-bar-container">
              <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
            </div>
            <div className="upload-progress-info">
              <p>
                {Math.round(((uploadProgress / 100) * imageFile.size) / 1024)} KB of{' '}
                {Math.round(imageFile.size / 1024)} KB uploaded
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUploadModal;
